interface ZoomSlotDimensions {
  width: number;
  height: number;
}

interface ZoomBaseDimensions {
  width: number;
  height: number;
}

interface ViewportSize {
  width: number;
  height: number;
}

const getInitialViewportValue = (value: number | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const parseUrl = (value: string) => {
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

const isSupportedTedUrl = (url: URL, allowedTedHosts: Set<string>) =>
  url.protocol === 'https:' &&
  !url.username &&
  !url.password &&
  allowedTedHosts.has(url.hostname.toLowerCase());

const parseHostname = (value: string) =>
  parseUrl(value)?.hostname.toLowerCase();

export const getAllowedTedHost = (
  configuredTedHost: unknown,
  fallbackTedHost: string
) => {
  if (typeof configuredTedHost === 'string') {
    return parseHostname(configuredTedHost) ?? parseHostname(fallbackTedHost);
  }

  return parseHostname(fallbackTedHost);
};

export const getInitialViewportSize = () => {
  if (typeof window === 'undefined') {
    return { width: 0, height: 0 };
  }

  return {
    width: getInitialViewportValue(window.innerWidth),
    height: getInitialViewportValue(window.innerHeight),
  };
};

export const sanitizeTedUrl = (
  value: string | undefined,
  allowedTedHost: string | undefined
) => {
  if (!value || !allowedTedHost) {
    return null;
  }

  const url = parseUrl(value);
  const allowedTedHosts = new Set([allowedTedHost]);

  return url && isSupportedTedUrl(url, allowedTedHosts) ? url.toString() : null;
};

export const isPhoneSizedViewport = (
  viewport: ViewportSize,
  mobileMaxViewportWidth: number,
  phoneShortSideMax: number
) => {
  if (viewport.width <= 0 || viewport.height <= 0) {
    return false;
  }

  return (
    viewport.width <= mobileMaxViewportWidth ||
    Math.min(viewport.width, viewport.height) <= phoneShortSideMax
  );
};

export const calculateMaxZoomPercentForSlot = (
  slot: ZoomSlotDimensions,
  base: ZoomBaseDimensions
) => {
  if (slot.width <= 0 || slot.height <= 0) {
    return 0;
  }

  const widthZoomPercent = Math.floor((slot.width / base.width) * 100);
  const heightZoomPercent = Math.floor((slot.height / base.height) * 100);

  return Math.max(0, Math.min(widthZoomPercent, heightZoomPercent));
};

export const calculateZoomSliderFillPercent = (
  zoomPercent: number,
  maxZoomPercent: number,
  effectiveMinZoomPercent: number
) => {
  if (maxZoomPercent <= effectiveMinZoomPercent) {
    return 0;
  }

  const zoomRange = maxZoomPercent - effectiveMinZoomPercent;
  const zoomProgress = zoomPercent - effectiveMinZoomPercent;
  return (zoomProgress / zoomRange) * 100;
};

export const calculateZoomSliderHandleCenterPx = (
  fillPercent: number,
  railWidth: number,
  handleWidth: number,
  handleOvershoot: number
) => {
  const minCenter = handleWidth / 2 - handleOvershoot;
  const maxCenter = railWidth - handleWidth / 2 + handleOvershoot;

  return minCenter + (maxCenter - minCenter) * (fillPercent / 100);
};
